/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import cx from 'classnames';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { isCpd } from '@ibm-watson/shared/modules/ia/util/config';
import { Instance as InstanceType } from '../../../../ts/instance';

import './Instance.scss';

const messages = defineMessages({
  accountLabel: {
    id: 'modules.ia.components.Instances.Instance.accountLabel',
    defaultMessage: 'Account:'
  },
  groupLabel: {
    id: 'modules.ia.components.Instances.Instance.groupLabel',
    defaultMessage: 'Resource Group:'
  },
  spaceLabel: {
    id: 'modules.ia.components.Instances.Instance.spaceLabel',
    defaultMessage: 'Space:'
  },
  regionLabel: {
    id: 'modules.ia.components.Instances.Instance.regionLabel',
    defaultMessage: 'Region:'
  },
  planLabel: {
    id: 'modules.ia.components.Instances.Instance.planLabel',
    defaultMessage: 'Plan:'
  },
  litePlan: {
    id: 'modules.ia.components.Instances.Instance.plans.lite',
    defaultMessage: 'Lite'
  },
  standardPlan: {
    id: 'modules.ia.components.Instances.Instance.plans.standard',
    defaultMessage: 'Standard'
  },
  plusPlan: {
    id: 'modules.ia.components.Instances.Instance.plans.plus',
    defaultMessage: 'Plus'
  },
  plusTrialPlan: {
    id: 'modules.ia.components.Instances.Instance.plans.plusTrial',
    defaultMessage: 'Trial'
  },
  premiumPlan: {
    id: 'modules.ia.components.Instances.Instance.plans.premium',
    defaultMessage: 'Premium'
  },
  enterprisePlan: {
    id: 'modules.ia.components.Instances.Instance.plans.enterprise',
    defaultMessage: 'Enterprise'
  },
  enterpriseIsolatedPlan: {
    id: 'modules.ia.components.Instances.Instance.plans.enterpriseIsolated',
    defaultMessage: 'Enterprise with Isolation'
  },
  otherPlan: {
    id: 'modules.ia.components.Instances.Instance.plans.other',
    defaultMessage: '{plan}'
  },
  regionUsSouth: {
    id: 'modules.ia.components.Instances.Instance.regions.usSouth',
    defaultMessage: 'Dallas'
  },
  regionUsEast: {
    id: 'modules.ia.components.Instances.Instance.regions.usEast',
    defaultMessage: 'Washington DC'
  },
  regionEuGb: {
    id: 'modules.ia.components.Instances.Instance.regions.euGb',
    defaultMessage: 'London'
  },
  regionEuDe: {
    id: 'modules.ia.components.Instances.Instance.regions.euDe',
    defaultMessage: 'Frankfurt'
  },
  regionAuSyd: {
    id: 'modules.ia.components.Instances.Instance.regions.auSyd',
    defaultMessage: 'Sydney'
  },
  regionJpTok: {
    id: 'modules.ia.components.Instances.Instance.regions.jpTok',
    defaultMessage: 'Tokyo'
  },
  regionKrSeo: {
    id: 'modules.ia.components.Instances.Instance.regions.krSeo',
    defaultMessage: 'Seoul'
  },
  regionOther: {
    id: 'modules.ia.components.Instances.Instance.regions.other',
    defaultMessage: '{region}'
  }
});

const messagesByRegion = {
  'us-south': messages.regionUsSouth,
  'us-east': messages.regionUsEast,
  'eu-gb': messages.regionEuGb,
  'eu-de': messages.regionEuDe,
  'au-syd:': messages.regionAuSyd,
  'jp-tok': messages.regionJpTok,
  'kr-seo': messages.regionKrSeo
};

const messagesByPlan = {
  lite: messages.litePlan,
  free: messages.litePlan,
  standard: messages.standardPlan,
  plus: messages.plusPlan,
  plus_trial: messages.plusTrialPlan,
  premium: messages.premiumPlan,
  enterprise: messages.enterprisePlan,
  enterprisedataisolation: messages.enterpriseIsolatedPlan
};

const getPlanMessage = (plan = '') => {
  let name = messagesByPlan[plan];
  if (!name && plan.indexOf('_') > -1) {
    const parts = plan.split('_', 1);
    name = messagesByPlan[parts[0]];
  }
  return name || messages.otherPlan;
};

const getRegionMessage = region => {
  const name = messagesByRegion[region];
  return name || messages.regionOther;
};

export interface InstanceProps {
  instance: InstanceType;
  className?: string;
}
const Instance: React.FC<InstanceProps> = ({ instance, className }) => {
  const instancePlanName = instance?.plan?.name || ' ';

  return (
    <div className={cx('Instance', className)}>
      <div className="Instance__header">{instance.name}</div>
      <div className="Instance__body">
        <div className="Instance__details">
          <div className="Instance__label">
            <FormattedMessage {...messages.accountLabel} />
          </div>
          <div className="Instance__detail">{instance.account.name}</div>
        </div>
        <div className="Instance__details">
          <div className="Instance__label">
            <FormattedMessage {...messages.regionLabel} />
          </div>
          <div className="Instance__detail">
            <FormattedMessage
              {...getRegionMessage(instance.region)}
              values={{ region: instance.region }}
            />
          </div>
        </div>
        <div className="Instance__details">
          <div className="Instance__label">
            <FormattedMessage {...messages.planLabel} />
          </div>
          <div className="Instance__detail">
            <FormattedMessage
              {...getPlanMessage(instancePlanName)}
              values={{ plan: instancePlanName }}
            />
          </div>
        </div>
        {!isCpd && (
          <div className="Instance__details">
            <div className="Instance__label">
              <FormattedMessage {...messages.groupLabel} />
            </div>
            <div className="Instance__detail">
              {instance.resourceGroup.name}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Instance;
